import { useState, useEffect, useRef } from 'react'
import chatService from 'src/components/chatbot/services/chatService'
import { createMessagePair, createUserMessage, createLoadingMessagePair } from 'src/components/chatbot/types/chatTypes'
import { replaceNamesWithIds, parseStoredMessageContent } from 'src/components/chatbot/utils/chatHelpers'
import axios from 'src/utils/axios'
import { API_BASE_URL } from 'src/constants'
import { useNavbarContext } from 'src/contexts/NavbarContext'
import useChatbotStore from 'src/components/chatbot/stores/chatbotStore'

const CONVERSATION_STATES = {
  NORMAL: 'normal',
  TOOL_ACTION_PENDING_SUCCESS: 'tool_action_pending_success',
  TOOL_ACTION_PENDING_CLARIFICATION: 'tool_action_pending_clarification',
  GENESUS_FILTER_PLANNING: 'genesus_filter_planning',
  GENESUS_FILTER_EXECUTED: 'genesus_filter_executed',
}

const findConversationState = (chatResponseObject) => {
  if (
    chatResponseObject?.interrupted === false &&
    chatResponseObject?.filter_called === false &&
    chatResponseObject?.filter_state === null
  ) {
    return CONVERSATION_STATES.NORMAL
  }

  if (
    chatResponseObject?.interrupted === true &&
    chatResponseObject?.filter_called === false &&
    chatResponseObject?.tools.length > 0
  ) {
    return CONVERSATION_STATES.TOOL_ACTION_PENDING_SUCCESS
  }

  if (
    chatResponseObject?.interrupted === true &&
    chatResponseObject?.filter_called === false &&
    chatResponseObject?.tools.length === 0
  ) {
    return CONVERSATION_STATES.TOOL_ACTION_PENDING_CLARIFICATION
  }

  if (chatResponseObject?.interrupted === false && chatResponseObject?.filter_called === true) {
    return CONVERSATION_STATES.GENESUS_FILTER_PLANNING
  }

  if (
    chatResponseObject?.interrupted === false &&
    chatResponseObject?.filter_called === false &&
    chatResponseObject?.filter_state !== null
  ) {
    return CONVERSATION_STATES.GENESUS_FILTER_EXECUTED
  }

  return CONVERSATION_STATES.NORMAL
}

const useChat = () => {
  // TODO we actually don't use sampleContext anymore, use fileId and sampleName instead individually
  const [sampleContext, setSampleContext] = useState(null) // { id: string, name: string }
  const [firstTableId, setFirstTableId] = useState(null)
  const { fileId, sampleName, currentFilters } = useNavbarContext()

  const getFileIdFromUrl = () => {
    const pathParts = window.location.pathname.split('/')
    if (
      pathParts.length >= 3 &&
      (pathParts[1] === 'variants' || pathParts[1] === 'unified' || pathParts[1] === 'dashboard')
    ) {
      return pathParts[2]
    }
    return null
  }
  const activeFileId = fileId || getFileIdFromUrl()
  const {
    chatId,
    setChatId,
    originalTools,
    setOriginalTools,
    isFilters,
    setIsFilters,
    chatMessageHistory,
    setChatMessageHistory,
    currentActionMessage,
    setCurrentActionMessage,
    setExecuteToolAction,
    setIsFirstMessageLoading,
    isBotResponding,
    setIsBotResponding,
    setShowChat,
    scrollToBottom,
    nameToId,
    idToName,
    chatConversationState,
    setChatConversationState,
  } = useChatbotStore()

  // const messagesContainerRef = useRef(null)
  const pollingIntervalRef = useRef(null)

  useEffect(() => {
    if (chatMessageHistory.length > 0) {
      setTimeout(scrollToBottom, 100)
    }
  }, [chatMessageHistory])

  useEffect(() => {
    if (isBotResponding === false && chatMessageHistory.length > 0) {
      setTimeout(scrollToBottom, 100)
    }
  }, [isBotResponding, chatMessageHistory.length])

  useEffect(() => {
    const fetchMetadata = async () => {
      if (fileId) {
        try {
          const response = await axios.get(`${API_BASE_URL}/vcf/${fileId}/redux`)
          const tables = response.data?.redux_state?.tables || []
          if (tables.length > 0) {
            setFirstTableId(tables[0].id)
          }
        } catch (error) {
          console.error('Error fetching sample metadata:', error)
        }
      }
    }

    fetchMetadata()
  }, [fileId])

  // Polling mechanism
  useEffect(() => {
    if (isBotResponding && chatId) {
      const pollAction = async () => {
        try {
          const actionData = await chatService.getLastAgentAction(chatId)
          if (actionData?.action) {
            setCurrentActionMessage(actionData.action)
          }
        } catch (error) {
          console.error('Error polling action:', error)
        }
      }

      pollingIntervalRef.current = setInterval(pollAction, 1000)

      pollAction()

      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null
        }
      }
    } else {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
      setCurrentActionMessage('')
    }
  }, [isBotResponding, chatId])

  // Update loading message when polling action
  useEffect(() => {
    if (isBotResponding && currentActionMessage && chatMessageHistory.length > 0) {
      const lastMessage = chatMessageHistory[chatMessageHistory.length - 1]
      if (lastMessage?.botMessage?.isLoading) {
        const updatedHistory = [...chatMessageHistory]
        updatedHistory[updatedHistory.length - 1] = {
          ...lastMessage,
          botMessage: {
            ...lastMessage.botMessage,
            actionMessage: currentActionMessage,
          },
        }
        setChatMessageHistory(updatedHistory)
      }
    }
  }, [currentActionMessage, isBotResponding])

  const handleToolAction = async (isFilters = false, toolCalls = null) => {
    if (!chatId) return

    const isToolsEdited = originalTools && toolCalls && JSON.stringify(originalTools) !== JSON.stringify(toolCalls)
    const acceptFlag = isToolsEdited ? false : true

    if (isFilters) {
      setExecuteToolAction(true)
      try {
        const response = await chatService.sendMessage(
          chatId,
          'Yes, go ahead.',
          currentFilters,
          fileId ? fileId : null,
          acceptFlag,
          toolCalls,
        )

        const currentConversationState = findConversationState(response)
        setChatConversationState(currentConversationState)

        let newMessagePair
        if (currentConversationState === CONVERSATION_STATES.GENESUS_FILTER_EXECUTED) {
          setIsFilters(false)
          if (response?.filter_state) {
            sessionStorage.setItem('VARIANT_FILTERS_STATE', JSON.stringify(response.filter_state))
          }
          newMessagePair = createMessagePair('Yes, go ahead.', response.response)
        } else if (currentConversationState === CONVERSATION_STATES.TOOL_ACTION_PENDING_SUCCESS) {
          setOriginalTools(response?.tools || null)
          setIsFilters(false)
          newMessagePair = createMessagePair('Yes, go ahead.', response.response, response?.tools || null)
        } else if (currentConversationState === CONVERSATION_STATES.TOOL_ACTION_PENDING_CLARIFICATION) {
          setIsFilters(false)
          newMessagePair = createMessagePair('Yes, go ahead.', response.response)
        } else if (currentConversationState === CONVERSATION_STATES.GENESUS_FILTER_PLANNING) {
          setOriginalTools(response?.tools || null)
          setIsFilters(true)
          newMessagePair = createMessagePair('Yes, go ahead.', response.response, response?.tools || null)
        } else {
          // Normal conversation
          newMessagePair = createMessagePair('Yes, go ahead.', response.response)
        }

        setChatMessageHistory([...chatMessageHistory, newMessagePair])
        setTimeout(scrollToBottom, 100)

        // console.log(response.filter_state)
        // TODO: This is a temporary solution to redirect to the variants page
        if (firstTableId) {
          const variantsUrl = `/libra/variants/${fileId}/${sampleName}/${firstTableId}?executeTools=1`
          window.location.href = variantsUrl
        }
      } catch (err) {
        console.error('Error executing tool calls:', err)
        // === Fallback message added ===
        const errorMessagePair = createMessagePair('Yes, go ahead.', 'An error occurred while processing your request.')
        setChatMessageHistory([...chatMessageHistory, errorMessagePair])
      } finally {
        setExecuteToolAction(false)
        setIsFilters(false)
      }
    } else {
      setExecuteToolAction(true)
      try {
        const response = await chatService.sendMessage(
          chatId,
          'Yes, go ahead.',
          currentFilters,
          fileId ? fileId : null,
          acceptFlag,
          toolCalls,
        )

        const currentConversationState = findConversationState(response)
        setChatConversationState(currentConversationState)

        let newMessagePair
        if (currentConversationState === CONVERSATION_STATES.GENESUS_FILTER_EXECUTED) {
          setIsFilters(false)
          if (response?.filter_state) {
            sessionStorage.setItem('VARIANT_FILTERS_STATE', JSON.stringify(response.filter_state))
          }
          newMessagePair = createMessagePair('Yes, go ahead.', response.response)
        } else if (currentConversationState === CONVERSATION_STATES.TOOL_ACTION_PENDING_SUCCESS) {
          setOriginalTools(response?.tools || null)
          setIsFilters(false)
          newMessagePair = createMessagePair('Yes, go ahead.', response.response, response?.tools || null)
        } else if (currentConversationState === CONVERSATION_STATES.TOOL_ACTION_PENDING_CLARIFICATION) {
          setIsFilters(false)
          newMessagePair = createMessagePair('Yes, go ahead.', response.response)
        } else if (currentConversationState === CONVERSATION_STATES.GENESUS_FILTER_PLANNING) {
          setOriginalTools(response?.tools || null)
          setIsFilters(true)
          newMessagePair = createMessagePair('Yes, go ahead.', response.response, response?.tools || null)
        } else {
          // Normal conversation
          newMessagePair = createMessagePair('Yes, go ahead.', response.response)
        }

        setChatMessageHistory([...chatMessageHistory, newMessagePair])
        setTimeout(scrollToBottom, 100)
        setIsBotResponding(false)
        setIsFirstMessageLoading(false)
      } catch (err) {
        console.error('Error executing tool action:', err)
        // === Fallback message added ===
        const errorMessagePair = createMessagePair('Yes, go ahead.', 'An error occurred while processing your request.')
        setChatMessageHistory([...chatMessageHistory, errorMessagePair])
      } finally {
        setExecuteToolAction(false)
      }
    }
  }

  const clearSelections = () => {
    if (window.setHpoTerms) {
      window.setHpoTerms([])
    }
    if (window.setDiseases) {
      window.setDiseases([])
    }
    if (window.setSelectedSamples) {
      window.setSelectedSamples([])
    }
    if (window.setSelectedPanels) {
      window.setSelectedPanels([])
    }
  }

  const restoreChat = async (threadId) => {
    try {
      const chatData = await chatService.getChatHistory(threadId)

      if (chatData?.external_messages) {
        const messagePairs = []

        // Group messages by pairs (human -> ai)
        for (let i = 0; i < chatData.external_messages.length; i += 2) {
          const humanMessage = chatData.external_messages[i]
          const aiMessage = chatData.external_messages[i + 1]

          if (humanMessage?.role === 'human' && aiMessage?.role === 'ai') {
            // Parse the human message to extract original content and chips
            const { originalMessage, selectedChips } = parseStoredMessageContent(humanMessage.content, idToName)
            const messagePair = createMessagePair(originalMessage, aiMessage.content, null, selectedChips)
            messagePairs.push(messagePair)
          } else if (humanMessage?.role === 'human') {
            // Parse the human message to extract original content and chips
            const { originalMessage, selectedChips } = parseStoredMessageContent(humanMessage.content, idToName)
            const messagePair = createMessagePair(originalMessage, 'Loading...', null, selectedChips)
            messagePairs.push(messagePair)
          }
        }

        setChatId(threadId)

        // Determine conversation state and adjust the last message pair to match the conversation state
        const restoredConversationState = findConversationState(chatData)
        setChatConversationState(restoredConversationState)
        setOriginalTools(chatData?.tools || null)

        if (messagePairs.length > 0) {
          const lastPairIdx = messagePairs.length - 1
          const lastPair = messagePairs[lastPairIdx]
          let newLastPair = lastPair
          if (restoredConversationState === CONVERSATION_STATES.GENESUS_FILTER_EXECUTED) {
            if (chatData?.filter_state) {
              sessionStorage.setItem('VARIANT_FILTERS_STATE', JSON.stringify(chatData.filter_state))
            }
            newLastPair = createMessagePair(
              lastPair.userMessage.content,
              lastPair.botMessage.content,
              null,
              lastPair.userMessage.selectedChips,
            )
          } else if (restoredConversationState === CONVERSATION_STATES.TOOL_ACTION_PENDING_SUCCESS) {
            newLastPair = createMessagePair(
              lastPair.userMessage.content,
              lastPair.botMessage.content,
              chatData?.tools || null,
              lastPair.userMessage.selectedChips,
            )
          } else if (restoredConversationState === CONVERSATION_STATES.TOOL_ACTION_PENDING_CLARIFICATION) {
            newLastPair = createMessagePair(
              lastPair.userMessage.content,
              lastPair.botMessage.content,
              null,
              lastPair.userMessage.selectedChips,
            )
          } else if (restoredConversationState === CONVERSATION_STATES.GENESUS_FILTER_PLANNING) {
            setIsFilters(true)
            newLastPair = createMessagePair(
              lastPair.userMessage.content,
              lastPair.botMessage.content,
              chatData?.tools || null,
              lastPair.userMessage.selectedChips,
            )
          } else {
            // Normal conversation
            newLastPair = createMessagePair(
              lastPair.userMessage.content,
              lastPair.botMessage.content,
              null,
              lastPair.userMessage.selectedChips,
            )
          }
          messagePairs[lastPairIdx] = newLastPair
        }

        setChatMessageHistory(messagePairs)

        setShowChat(true)

        setTimeout(scrollToBottom, 100)

        return true
      }
      return false
    } catch (error) {
      console.error('Error restoring chat:', error)
      return false
    }
  }

  const handleSendMessage = async (userContent, editedMessageIndex = null) => {
    if (!userContent) return

    clearSelections()

    // This check is to make it backward compatible with the old format of the messages
    let displayMessage, backendMessage, selectedChips
    if (typeof userContent === 'string') {
      displayMessage = userContent.trim()
      backendMessage = userContent.trim()
      selectedChips = null
    } else {
      displayMessage = userContent.displayMessage?.trim()
      backendMessage = userContent.backendMessage?.trim()
      selectedChips = userContent.selectedChips || null
    }

    if (!displayMessage || !backendMessage) return

    const processedMessage = replaceNamesWithIds(backendMessage, nameToId)
    const userMessage = createUserMessage(displayMessage, chatMessageHistory.length, selectedChips)

    try {
      // if chat is not started, create new chat, else continue from before
      if (!chatId) {
        setIsFirstMessageLoading(true)
        setIsBotResponding(true)
        setShowChat(true)

        // immediately show user message with a loading placeholder for bot
        setChatMessageHistory([...chatMessageHistory, createLoadingMessagePair(userMessage)])
        setTimeout(scrollToBottom, 100)

        const newChatResponse = await chatService.createChat(
          processedMessage,
          currentFilters,
          activeFileId ? activeFileId : null,
        )

        const currentConversationState = findConversationState(newChatResponse)
        setChatConversationState(currentConversationState)

        setChatId(newChatResponse.id)

        let newMessagePair
        if (currentConversationState === CONVERSATION_STATES.GENESUS_FILTER_EXECUTED) {
          setIsFilters(false)
          // This case won't probably happen when creating a chat
          if (newChatResponse?.filter_state) {
            sessionStorage.setItem('VARIANT_FILTERS_STATE', JSON.stringify(newChatResponse.filter_state))
          }
          newMessagePair = createMessagePair(displayMessage, newChatResponse.response, null, selectedChips)
        } else if (currentConversationState === CONVERSATION_STATES.TOOL_ACTION_PENDING_SUCCESS) {
          setIsFilters(false)
          newMessagePair = createMessagePair(
            displayMessage,
            newChatResponse.response,
            newChatResponse?.tools || null,
            selectedChips,
          )
        } else if (currentConversationState === CONVERSATION_STATES.TOOL_ACTION_PENDING_CLARIFICATION) {
          setIsFilters(false)
          newMessagePair = createMessagePair(displayMessage, newChatResponse.response, null, selectedChips)
        } else if (currentConversationState === CONVERSATION_STATES.GENESUS_FILTER_PLANNING) {
          setIsFilters(true)
          newMessagePair = createMessagePair(
            displayMessage,
            newChatResponse.response,
            newChatResponse?.tools || null,
            selectedChips,
          )
        } else {
          // Normal conversation
          newMessagePair = createMessagePair(displayMessage, newChatResponse.response, null, selectedChips)
        }

        setOriginalTools(newChatResponse?.tools || null)

        setChatMessageHistory([...chatMessageHistory, newMessagePair])
        setTimeout(scrollToBottom, 100)
        setIsBotResponding(false)
        setIsFirstMessageLoading(false)
      } else {
        setIsBotResponding(true)

        setChatMessageHistory([...chatMessageHistory, createLoadingMessagePair(userMessage)])
        setTimeout(scrollToBottom, 100)

        let botResponse

        if (chatConversationState === CONVERSATION_STATES.TOOL_ACTION_PENDING_SUCCESS) {
          botResponse = await chatService.sendMessage(
            chatId,
            processedMessage,
            currentFilters,
            fileId ? fileId : null,
            false,
          )
        } else if (chatConversationState === CONVERSATION_STATES.GENESUS_FILTER_PLANNING) {
          botResponse = await chatService.sendMessage(
            chatId,
            processedMessage,
            currentFilters,
            fileId ? fileId : null,
            false,
          )
        } else {
          // Normal conversation
          botResponse = await chatService.sendMessage(
            chatId,
            processedMessage,
            currentFilters,
            fileId ? fileId : null,
            false,
          )
        }

        const currentConversationState = findConversationState(botResponse)
        setChatConversationState(currentConversationState)

        let newMessagePair
        if (currentConversationState === CONVERSATION_STATES.GENESUS_FILTER_EXECUTED) {
          setIsFilters(false)
          if (botResponse?.filter_state) {
            sessionStorage.setItem('VARIANT_FILTERS_STATE', JSON.stringify(botResponse.filter_state))
          }
          newMessagePair = createMessagePair(displayMessage, botResponse.response, null, selectedChips)
        } else if (currentConversationState === CONVERSATION_STATES.TOOL_ACTION_PENDING_SUCCESS) {
          setIsFilters(false)
          newMessagePair = createMessagePair(
            displayMessage,
            botResponse.response,
            botResponse?.tools || null,
            selectedChips,
          )
        } else if (currentConversationState === CONVERSATION_STATES.TOOL_ACTION_PENDING_CLARIFICATION) {
          setIsFilters(false)
          newMessagePair = createMessagePair(displayMessage, botResponse.response, null, selectedChips)
        } else if (currentConversationState === CONVERSATION_STATES.GENESUS_FILTER_PLANNING) {
          setIsFilters(true)
          newMessagePair = createMessagePair(
            displayMessage,
            botResponse.response,
            botResponse?.tools || null,
            selectedChips,
          )
        } else {
          // Normal conversation
          newMessagePair = createMessagePair(displayMessage, botResponse.response, null, selectedChips)
        }

        setOriginalTools(botResponse?.tools || null)

        setChatMessageHistory([...chatMessageHistory, newMessagePair])
        setTimeout(scrollToBottom, 100)
        setIsBotResponding(false)
      }
    } catch (err) {
      console.error('Error sending message:', err)
      // === Fallback message added ===
      const errorMessagePair = createMessagePair(
        displayMessage || 'You',
        'Oops! Something went wrong while processing your request.',
      )
      setChatMessageHistory([...chatMessageHistory, errorMessagePair])

      setIsBotResponding(false)
      setIsFirstMessageLoading(false)
    }
  }

  return {
    handleSendMessage,
    handleToolAction,
    restoreChat,
    clearChatState: () => {
      setChatId(null)
      setChatMessageHistory([])
      setSampleContext(null)
      setChatConversationState(CONVERSATION_STATES.NORMAL)
      setOriginalTools(null)
      // Clear session storage when starting a new chat
      const { clearSessionStorage } = useChatbotStore.getState()
      clearSessionStorage()
    },
    setVisibility: (isVisible) => {},
  }
}

export default useChat
